import { useBoundingClientRect } from '~/hooks/useBoundingClientRect'
import { assignRef } from '~/common/reactUtils'
import { cq } from '~/common/containerQuery'
import { useIsSanaRebrandEnabled } from '~/theme/useIsSanaRebrandEnabled'
import { forwardRef } from '../../design_system/chakra'
import { Box, BoxProps } from '../../design_system'

export type StickyHeightExpanderProps = BoxProps & {
  offsetTopPx?: number
  offsetBottomPx?: number
}

const HEADER_HEIGHT = 60

/**
 * StickyHeightExpander is a React component that expands its height to fill the viewport,
 * accounting for sticky headers and optional top/bottom offsets. It uses a bounding client rect
 * to dynamically set its height so that content below a sticky header remains visible and scrollable.
 *
 * @param {number} [offsetTopPx=HEADER_HEIGHT] - The number of pixels to offset from the top (defaults to header height).
 * @param {number} [offsetBottomPx] Extra pixels from the bottom. Negative grows past the viewport (rebrand default -12).
 * @param {BoxProps} props - Additional Box props to pass to the underlying Box component.
 * @returns {JSX.Element} A div component with sticky positioning and dynamic height.
 */
export const StickyHeightExpander = forwardRef<StickyHeightExpanderProps, 'div'>(
  ({ offsetTopPx = HEADER_HEIGHT, offsetBottomPx: _offsetBottomPx, sx, ...props }, ref) => {
    const isRebrand = useIsSanaRebrandEnabled()
    const offsetBottomPx = _offsetBottomPx ?? (isRebrand ? -12 : 0)
    const clientRectRef = useBoundingClientRect((rect, el) => {
      el.style.setProperty('--rectY', `${rect.y}px`)
      el.style.setProperty('--offsetTopPx', `${offsetTopPx}px`)
      el.style.setProperty('--offsetBottomPx', `${offsetBottomPx}px`)
    })

    return (
      <Box
        ref={el => {
          clientRectRef?.(el)
          assignRef(ref, el)
        }}
        data-sticky-height-expander
        sx={cq({
          top: 'var(--offsetTopPx)',
          transform: 'translateZ(0)',
          willChange: 'height, top',
          position: 'sticky',
          height: 'calc(100vh - max(var(--offsetTopPx), var(--rectY)) + min(0px, var(--offsetBottomPx)))',
          ...sx
        })}
        {...props}
      />
    )
  }
)
